// App.jsx — Anamoria SPA
// v1.47 — NEW-175 (Leader Home, increment B): a provisioned leader with no spaces lands on /leader
//   instead of /spaces/new (bootstrap redirect here + ConsentPage). Safe no-op for non-leaders. (Nov 2, 2026)
// v1.46 — NEW-121: contributor video capture route (2026-09-27)
//   Added protected /contributing/:spaceId/video → ContributorVideoPage (mode="linked"),
//   same pattern as the other 3 contributor capture routes. Flag-gated server-side +
//   client-side (contributor_video_enabled); no route-level gating needed here since
//   the page guards its own mount and the nav button that reaches it is itself
//   conditional. Purely additive — zero changes to any existing route.
// v1.45 — NEW-93: keep the opt-in remembered email fresh (login_hint pre-fill).
// v1.44 — NEW-65 (PR-7b): read-only contributor memory detail route
//   /contributing/:spaceId/memories/:memId → ContributorMemoryDetailPage (view-only,
//   memory passed via router state; contributor-scoped auth). MemoryDetailPage untouched.
// v1.43 — settings overlay-on-feed: removed the settings_overlay flag gate
//   (pre-launch, no users — the dark-deploy/canary safety bought nothing and only
//   added plumbing). The overlay is now the default: /settings renders over the
//   triggering page whenever navigated with state.backgroundLocation. Direct
//   /settings / refresh still fall back to the full page.
// v1.42 — settings_overlay (PR1): /settings can render as an overlay over the
//   page it was triggered from, via a backgroundLocation modal-route. Server
//   flag settings_overlay (fail-closed); off ⇒ backgroundLocation null ⇒ the
//   single <Routes> renders exactly as before. Overlay hosts <SettingsPage
//   inOverlay/> in <SettingsOverlay>. Plan: settings-overlay-model-plan v0.2.
// v1.41 — NEW-21 (PIN lock screen, SPA half): three additions, no behaviour
//   change to anything that existed. (a) pass securitySettings.pinLength to
//   <LockScreen> so it can draw one slot per digit of the member's actual PIN
//   instead of a fixed six — the value is ALREADY arriving in the
//   /security/settings response fetched below and was simply unused.
//   (b) NEW handlePinLocked callback — sets lockReason to 'pin_locked', the
//   same shape as handleSessionExpired. Deliberately a THIRD reason rather
//   than reusing 'session_expired': a locked PIN and an expired session are
//   different events and must not share a flag, or a later change will put
//   the wrong words on the wrong screen. (c) pass onPinLocked to <LockScreen>.
//   Plan: new21-pin-lockscreen-spa-plan-DRAFT-for-review-v0.3.md
//   Copy: Anamoria_PIN_Lock_Screen_Copy_v0.5.md
//   Backend half already live: anamoria-security v1.1 + migration 040.
// v1.40 — NEW-16 flash fix: v1.39 ran the /me/contributions lookup AFTER setAppState,
//   so bootstrapped flipped true while the ~500ms lookup was still in flight and the
//   app briefly rendered /spaces/new (the owner create screen) at the root URL before
//   navigating to the contributor feed — a visible multi-screen flicker on fresh
//   contributor login (confirmed via DevTools trace: ModuleEvaluated /spaces/new
//   mid-lookup). Fix: hoist the lookup ABOVE setAppState so bootstrapped stays false
//   (loading gate stays up) until the destination is known; setAppState + navigate
//   then fire together with no intermediate screen. Behaviour otherwise identical.
// v1.39 — NEW-16 (returning-contributor routing): completes the piece deferred in
//   v1.38. On fresh login a pure contributor owns no space, so /spaces returns empty
//   and the success-path redirect sent them to /spaces/new (owner-create, 403 dead
//   end). Fix: in the spaces.length === 0 branch, before defaulting to /spaces/new,
//   look up GET /me/contributions (existing JWT route, handleMyContributions) and, if
//   the caller is linked to any contributor space, navigate to
//   /contributing/{spaceId}/memories (first contribution). Lookup failure is
//   non-fatal — falls through to /spaces/new (prior behaviour), so login never blocks.
//   Frontend-only; no backend/route/migration (endpoint already live from NEW-15).
//   Owner path untouched (lookup only runs when no owned space). The unaffiliated
//   case (no owned space + no contribution) still lands on /spaces/new (NEW-18).
// v1.38 — Layer 4 (NEW-15): contributor role-aware bootstrap. Two carve-outs so
//   the startup redirect stops fighting the contributor claim/feed: (1) 404
//   USER_NOT_FOUND on /invite/* or /contributing/* no longer bounces to /join
//   before the auto-link mints the users row; (2) the success-path redirect no
//   longer sends a contributor (no owned space) to /spaces/new when they're on
//   /contributing/*. Additive; mirrors the v1.28 invite guard. Returning cold
//   contributor nav (root-load) deferred — needs backend linked-space lookup.
// v1.37 — B3: /spaces/:spaceId/invite/bulk route + BulkInvitePage import
//   (dark; entry points gate on group_spaces_enabled; single InvitePage
//   route unchanged). Additive.
// v1.36 — 3.2: MomentDetailPage route (July 21, 2026)
//   New route /spaces/:spaceId/moments/:momentId (D-3.8, ProtectedRoute),
//   mirroring the legacy /memories/:memId block. Dark by construction: the
//   only inbound link is the flagged feed, and GET /moments/{id} is
//   server-fail-closed 404 for flag-off users (no SPA flag check needed —
//   momentdetail-3-2-DRAFT-for-review v0.2 §3). Nothing else touched.
// v1.35 — 2.1c: capture spaces v1.7 `flags` envelope into appState (July 19, 2026)
//   Bootstrap previously dropped `flags` on the floor (v1.34 read only
//   .spaces). Now stored at all three setAppState sites; absent/old envelope
//   ⇒ {} ⇒ moments_enabled falsy ⇒ legacy UI (fail-closed by construction,
//   D1). Context shape unchanged — appState spread carries the new field
//   (v1.23 precedent). Consumed by SpacePage v2.29. Nothing else touched.
//   Per addmemory-2.1c-entry-footer-DRAFT-for-review-v0.2.md.
// v1.34 — 2.1a: AddMemoryPage scaffold route /spaces/:spaceId/add (July 19, 2026)
//   Dark: route + import only, no entry links (entry + footer = 2.1c, flag-gated
//   on spaces v1.7 flags.moments_enabled). D7 route. Also: bootstrap deep-link
//   guard extended to /spaces/:id/add (v1.28 pattern) so the dark route survives
//   a full page load — approved by Sophie 2026-07-19 after the feed-bounce repro.
//   Nothing else touched; see addmemorypage-2.1a-scaffold-DRAFT-for-review-v0.2.md.
// v1.33 — Root "/" route: browser Back to the app root resolves to the user's
//   space (or /spaces/new) instead of the /join catch-all — fixes the back button
//   logging authenticated users out (July 5, 2026). Ported from staging v1.29's
//   root route; the rest of account-link v1.29-v1.31 is superseded by the Phase 2 rebuild.
// v1.32 — Tour prompt stops re-firing: added updateOnboarding context action to
//   sync appState.onboardingComplete after tour completion/dismiss (July 5, 2026).
//   (v1.29-v1.31 are the account-link fixes on the diverged staging branch, to be
//   re-applied in Phase 2; numbered past them here to avoid a version collision.)
// v1.28 — Bootstrap no longer hijacks the invite/connect flow (June 28, 2026)
//   - useSessionBootstrap previously redirected EVERY authenticated load to the
//     user's own first space (/spaces/:id), with no path guard. An invited member
//     who clicked their invite link while signed in was bounced to their own space
//     and never reached the "Connect to your account" step (F3), so the invite
//     stayed unlinked (linked_user_id NULL). Now: when the user is on /invite/:token
//     the success-path redirect is skipped so the invite page can run. Pairs with
//     ContributorLandingPage v1.6 (F3). (New-user USER_NOT_FOUND branch is a
//     separate follow-on.)
//
// v1.27 — Lock screen persists across refresh via sessionStorage (June 23, 2026)
// Changes from v1.26:
//   - locked/lockReason state initializers read from sessionStorage('ana_locked').
//     Prevents bypassing an active PIN lock screen by refreshing the page.
//   - When locking with reason 'pin': writes sessionStorage('ana_locked', 'true').
//   - On unlock: removes 'ana_locked' from sessionStorage.
//   - On session expired transition: removes 'ana_locked' (Auth0 redirect handles
//     re-auth; persisting 'session_expired' would cause stale lock after re-login).
//   - sessionStorage scope: persists across refresh, clears on tab close.
//     Existing logout handlers in LockScreen already clear all ana_* keys.
//   - No changes to LockScreen.jsx or any other file.
//
// v1.26 — Public /privacy route (June 14, 2026)
// Changes from v1.25:
//   - Imported LegalPage (static import, same pattern as ConsentPage).
//   - Added ONE public route: /privacy → <LegalPage doc="privacy" />, placed
//     in the Public block above the *→/join fallback. LegalPage bundles the
//     published Privacy Policy (src/legal/privacy-policy-1.0.md) so it renders
//     offline via the existing SPA navigateFallback — no service-worker change.
//   - /terms intentionally NOT added yet (Terms held until counsel completes it).
//   - Purely additive: zero changes to any existing route, the *→/join
//     fallback, bootstrap, appState, context, auth, or lock-screen logic.
//
// v1.25 — Contributor account-link linked routes (May 31, 2026)
// Changes from v1.24:
//   - Added 4 protected /contributing/:spaceId/{memories,record,write,photo}
//     routes (account-link Phase 1, sub-phase F1). Each wraps the existing
//     contributor page component in <ProtectedRoute> and passes mode="linked"
//     so the page uses the owner JWT client + /me/contributions prefix.
//   - The public /contribute/* routes and ALL other routes are unchanged.
//   - No bootstrap, appState, context, or auth changes.
//
// v1.24 — Install guides wired in (May 29, 2026)
// Changes from v1.23:
//   - Imported IOSInstallPrompt and AndroidInstallPrompt.
//   - Rendered both, gated on appState.onboardingComplete, as siblings of
//     PWAUpdatePrompt (so they never interrupt first-time onboarding and
//     never show on /join). Each component self-gates by platform + standalone
//     mode + localStorage dismissal.
//   - No route, auth, bootstrap, context, or lock-screen changes.
//
// v1.23 — Onboarding tour state in appState (May 22, 2026)
// Changes from v1.22:
//   - Added onboardingComplete and tourPromptDismissals to appState
//     initial shape (default false/0).
//   - Added both fields to bootstrap setAppState (from /pilot/me response).
//   - Added both fields to 409 retry setAppState path.
//   - No context shape change — context spreads all of appState via
//     { ...appState, ... } so new fields flow automatically.
//   - Zero route changes, zero component changes, zero other logic changes.
//
// v1.22 — LockScreen onSessionExpired prop (May 20, 2026)
// Changes from v1.21:
//   - NEW: handleSessionExpired callback — sets lockReason to 'session_expired'.
//     One-way transition within a lock cycle (cannot revert to 'pin').
//     Called by LockScreen when PIN verification returns 401/403.
//   - Pass onSessionExpired={handleSessionExpired} to <LockScreen>.
//   - No other logic changes.
//
// v1.21 — Lock screen: visibilitychange listener + LockScreen overlay (May 18, 2026)
// Changes from v1.20:
//   - NEW import: LockScreen component (PIN unlock + session expired overlay).
//   - NEW state: locked, lockReason, securitySettings (inside AppRoutes).
//   - NEW ref: lastHiddenAt — records timestamp when app is backgrounded.
//   - NEW effect: fetches GET /security/settings after bootstrap completes
//     (hasPin, lockTimeoutSeconds). Determines if lock screen activates.
//   - NEW effect: visibilitychange listener. On return from background:
//     (1) elapsed < lockTimeout → no lock, resume normally.
//     (2) isAuthenticated false → session expired screen (Tier 2).
//         Uses synchronous Auth0 SDK boolean — no iframe/network call.
//     (3) User has PIN → show lock screen with PIN pad (Tier 1).
//   - NEW: <LockScreen> rendered inside AppContext.Provider, above Suspense
//     and Routes, when locked === true. Covers all content with solid overlay.
//   - NEW: getApiForLock — creates API client for LockScreen PIN verification.
//     Same createApiClient(getAccessTokenSilently) pattern as SettingsPage.
//   - Zero route changes, zero bootstrap changes, zero context shape changes.
//   - Zero changes to updateProfile, updateSpaces, updateConsent callbacks.
//
// v1.20 — May 16, 2026 — Video recording route
// Changes from v1.19:
//   - Added VideoPage import and /spaces/:spaceId/video protected route
//     (WS-6 Step 6.1 per Video Recording Implementation Plan v1.0).
//   - Zero bootstrap changes, zero context changes, zero other route changes.
//
// Previous changes (v1.19): Surface consent record in appState
// Changes from v1.18:
//   - appState now includes consentDate and consentPolicyVersion. The Lambda's
//     GET /pilot/me has been returning these since pilotaccessindex.mjs v1.5
//     (LATERAL JOIN against consent_records), but the bootstrap was dropping
//     them on the floor. SettingsPage Account panel reads them — without this
//     fix it always shows "No consent record on file."
//   - Three setAppState blocks updated to capture the fields:
//       (1) Returning user — Promise.all(/pilot/me, /spaces) success
//       (2) New user post-join — joinData has no consent yet, set both null
//       (3) 409 retry — same as (1)
//   - NEW: updateConsent context callback — whitelists consentDate /
//     consentPolicyVersion. Called by ConsentPage v1.2 after successful
//     POST /pilot/consent so the Settings panel reflects the new consent
//     immediately, no hard refresh required. Uses createdAt from the Lambda
//     response (DB-authoritative, no client-side fabrication, no extra HTTP).
//   - Pattern mirrors existing updateProfile callback exactly.
//   - Zero route changes, zero auth changes, zero Lambda changes.
//
// Previous changes (v1.18): Personal invite token support
// Changes from v1.17:
//   - Bootstrap join flow passes ana_inviteToken from sessionStorage to
//     POST /pilot/join (inviteToken field). Token is claimed atomically
//     with user creation on the server.
//   - NEW error: EMAIL_MISMATCH — shown when Auth0 login email doesn't
//     match the invite token's email. Non-retryable, with "Start over" link.
//   - NEW route: /join/invite/:token → JoinPage (public, no auth)
//   - sessionStorage cleanup includes ana_inviteToken after successful join.
//
// Previous changes (v1.17): BP-1a Fix 5: Bootstrap join error recovery (April 27, 2026)
// Changes from v1.16:
//   - 409 USER_ALREADY_EXISTS recovery: retry GET /pilot/me + /spaces,
//     route normally instead of showing generic error screen.
//   - 403 join errors (GROUP_NOT_ACTIVE, EMAIL_NOT_ALLOWED, USER_DEACTIVATED):
//     show specific empathetic message + "Start over" link to /join.
//     These are non-retryable — retrying won't help.
//   - bootstrapError shape changed from string to object:
//     { code, message, retryable } — no downstream consumers affected
//     (only read by the error screen in AppRoutes).
//   - Generic bootstrap error screen updated:
//     "We couldn't open Anamoria just now." + "Your memories are safe."
//   - Zero route changes, zero import changes, zero context shape changes.
//
// Previous changes (v1.16):
//   - Added PWAUpdatePrompt import + render (shows "new version available" banner)
//   - Zero route changes, zero auth changes, zero bootstrap changes.
//
// Previous changes (v1.15):
//   - A-1: Parallelize bootstrap — /pilot/me + /spaces via Promise.all
//     (~600-1,000ms cold, ~200ms warm savings vs serial awaits)
//   - A-4: Added updateSpaces callback to AppContext so SpacePage sidebar
//     and CreateSpaceModal can update the spaces list without re-fetching.
//   - Zero route changes, zero auth changes.
//
// Previous changes (v1.14):
//   - Added MyRequestsPage import and /settings/my-requests protected route
//   - Zero bootstrap or context changes
//
// Previous changes (v1.12):
//   - useSessionBootstrap returns { appState, setAppState }
//   - updateProfile callback in context
//
// Previous changes (v1.11):
//   - Added 4 static imports for new contributor pages:
//     ContributorHomePage, ContributorRecordPage, ContributorWritePage,
//     ContributorPhotoPage (all in pages/).
//   - Restructured contributor routing to match LWC axr_MemoryVaultV2
//     two-screen flow + three capture pages:
//       /contribute/:spaceId            → ContributorHomePage   (CHANGED
//         from ContributorFeedPage — landing page with "You've been
//         invited to contribute" CTAs)
//       /contribute/:spaceId/memories   → ContributorFeedPage   (NEW route
//         — feed screen reached via "View shared memories →" link from
//         HomePage; back chevron on feed returns to HomePage)
//       /contribute/:spaceId/record     → ContributorRecordPage (NEW)
//       /contribute/:spaceId/write      → ContributorWritePage  (NEW)
//       /contribute/:spaceId/photo      → ContributorPhotoPage  (NEW)
//     All 5 contributor routes remain in the "Public (no auth)" block
//     because contributors authenticate via session token (in sessionStorage,
//     set by ContributorLandingPage.jsx on claim), not Auth0 JWT.
//     ProtectedRoute would require a JWT and reject contributors.
//   - No other changes: no bootstrap logic changes, no changes to any other
//     route, no context changes, no hook changes, no component changes
//     outside imports and the contributor route block.
//
// React Router v6 matches routes by specificity, not declaration order, so
// /contribute/:spaceId/memories is correctly distinguished from
// /contribute/:spaceId without path ordering gymnastics. The ordering below
// is purely hierarchical for readability.
//
// Auth strategy for contributor routes (unchanged pattern from v1.10):
//   - No ProtectedRoute wrapper.
//   - Each contributor page component reads ana_sessionToken from
//     sessionStorage via getSessionToken() helper on mount.
//   - If no token present, page renders "session expired" state with
//     guidance to re-use invite link.
//   - API calls flow through createContributorApiClient (api/contributorApi.js)
//     which injects x-session-token + x-api-key headers.
//
// Previous changes (v1.10):
//   - Added FeedbackPage route + sessionTag context (Session 2).
//
// Previous changes (v1.9):
//   - Added ReminderPage route (Session 1A).
//
// [Earlier change history unchanged from v1.10 header.]

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import AuthProvider from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import { createApiClient } from './api/client';
import { setRememberedEmail } from './utils/rememberEmail';

import JoinPage from './pages/JoinPage';
import ConsentPage from './pages/ConsentPage';
import LegalPage from './pages/LegalPage';
import CreateSpacePage from './pages/CreateSpacePage';
import SpacePage from './pages/SpacePage';
import RecordPage from './pages/RecordPage';
import WritePage from './pages/WritePage';
import PhotoPage from './pages/PhotoPage';
import VideoPage from './pages/VideoPage';
// v1.34: 2.1a — unified capture scaffold (dark; entry + footer land in 2.1c).
import AddMemoryPage from './pages/AddMemoryPage';
import MemoryDetailPage from './pages/MemoryDetailPage';
// v1.36: 3.2 — read-only moment detail (D-3.6/D-3.8).
import MomentDetailPage from './pages/MomentDetailPage';
import InvitePage from './pages/InvitePage';
import BulkInvitePage from './pages/BulkInvitePage';
import ContributorLandingPage from './pages/ContributorLandingPage';
// v1.11: Session 3 — contributor landing page (Screen 1 per LWC
// axr_MemoryVaultV2 contributor-landing-screen block).
// Track B (B-2): ContributorHomePage retired (session-only home; no linked equivalent —
//   the feed is the linked entry). Import removed.
import ContributorFeedPage from './pages/ContributorFeedPage';
// v1.11: Session 3 — contributor capture pages.
import ContributorRecordPage from './pages/ContributorRecordPage';
import ContributorWritePage from './pages/ContributorWritePage';
import ContributorPhotoPage from './pages/ContributorPhotoPage';
import ContributorVideoPage from './pages/ContributorVideoPage'; // NEW-121
import ContributorMemoryDetailPage from './pages/ContributorMemoryDetailPage';
import LeaderPage from './pages/LeaderPage';
import SettingsPage from './pages/SettingsPage';
import SettingsOverlay from './components/SettingsOverlay';   // v1.42: settings overlay-on-feed (PR1)
import UpgradePage from './pages/UpgradePage';
import ReminderPage from './pages/ReminderPage';
import FeedbackPage from './pages/FeedbackPage';
// v1.14: Phase D — full request history page
import MyRequestsPage from './pages/MyRequestsPage';
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
import UpgradeSuccessPage from './pages/UpgradeSuccessPage';

import ButterflyLoader from './components/ButterflyLoader';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import IOSInstallPrompt from './components/IOSInstallPrompt';
import AndroidInstallPrompt from './components/AndroidInstallPrompt';
import LockScreen from './components/LockScreen';

// ─── App context — shared state across all pages ──────────────────────────

const AppContext = createContext(null);
export function useAppContext() {
  return useContext(AppContext);
}

// ─── Placeholder pages (replaced step by step) ───────────────────────────

function PlaceholderPage({ name }) {
  return (
    <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#2d3436' }}>
      <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7c9885' }}>
        Anamoria
      </p>
      <h2 style={{ marginTop: 8, fontSize: 20 }}>{name}</h2>
      <p style={{ marginTop: 8, fontSize: 14, color: '#737373' }}>
        This screen is built in a later step.
      </p>
    </div>
  );
}

function LoadingFallback() {
  return <ButterflyLoader />;
}

// ─── Session bootstrap ────────────────────────────────────────────────────
// Runs once after Auth0 confirms the user is authenticated.
// Identity Bootstrap Plan v1.0:
//   - GET /pilot/me first (DB is source of truth)
//   - 200 = returning user → set appState, load spaces, route
//   - 404 = new user → check sessionStorage for groupId → POST /pilot/join
//   - localStorage is never read or written for identity keys
//
// v1.17 (Fix 5): Join error recovery:
//   - 409 USER_ALREADY_EXISTS → retry /me + /spaces → route normally
//   - 403 GROUP_NOT_ACTIVE / EMAIL_NOT_ALLOWED / USER_DEACTIVATED →
//     specific empathetic message, non-retryable, "Start over" link
//   - All other errors → generic retryable error screen
//   - bootstrapError shape: { code, message, retryable } (was string)
//
// v1.12: Returns { appState, setAppState } instead of appState alone.
// setAppState is consumed by AppRoutes to create the updateProfile callback.

function useSessionBootstrap(isAuthenticated, getAccessTokenSilently, auth0User) {
  const navigate = useNavigate();
  const [appState, setAppState] = useState({
    bootstrapped: false,
    bootstrapError: null,
    userId: null,
    displayName: null,
    email: null,
    pilotRole: null,
    groupId: null,
    groupName: null,
    consentDate: null,
    consentPolicyVersion: null,
    spaces: [],
    flags: {},            // v1.35: spaces v1.7 flag envelope (fail-closed default)
    currentSpace: null,
    onboardingComplete: false,
    tourPromptDismissals: 0,
  });

  const bootstrap = useCallback(async () => {
    const api = createApiClient(getAccessTokenSilently);

    try {
      // A-1: Parallel bootstrap — /pilot/me and /spaces are independent.
      // Saves ~600-1,000ms cold, ~200ms warm vs serial awaits.
      const [meData, spacesData] = await Promise.all([
        api.get('/pilot/me'),
        api.get('/spaces'),
      ]);
      const spaces = spacesData.spaces || [];
      const currentSpace = spaces[0] || null;

      // Path guards — computed BEFORE setAppState so the NEW-16 lookup below can
      // reuse them and skip when we're not going to redirect.
      // v1.28: Do not hijack the invite/connect flow. An invited member who is
      // signed in must stay on /invite/:token so ContributorLandingPage's
      // "Connect to your account" card (F3) can link the invite. Without this
      // guard, bootstrap redirected them to their own space and the invite was
      // never connected (linked_user_id stayed NULL).
      const onInviteFlow = window.location.pathname.startsWith('/invite/');
      // v1.38 (Layer 4, NEW-15): a contributor (pilot_role 'contributor') owns no
      // space, so the redirect below would send them to /spaces/new (the owner
      // surface, now 403 server-side). Don't hijack a load that's already on the
      // linked contributor feed — same carve-out pattern as the v1.28 invite guard.
      const onContributing = window.location.pathname.startsWith('/contributing/');
      // v1.34: 2.1a — preserve deep links to the dark capture route; the only
      // way to reach it until 2.1c adds the flag-gated entry (same pattern as
      // the v1.28 invite carve-out).
      const onAddMemory = /^\/spaces\/[^/]+\/add$/.test(window.location.pathname);

      // v1.40 (NEW-16): resolve the contributor redirect target BEFORE flipping
      // bootstrapped. v1.39 did this lookup AFTER setAppState, so during the ~500ms
      // /me/contributions round-trip the app rendered as bootstrapped at the root
      // URL and briefly flashed the /spaces/new create screen before jumping to the
      // feed (confirmed in a DevTools trace: ModuleEvaluated /spaces/new mid-lookup).
      // Doing it here keeps bootstrapped false — the loading gate stays up — until we
      // know the destination, so setAppState + navigate fire together with no wrong
      // screen in between. Only runs when there's no owned space AND we're actually
      // going to redirect. Lookup failure is non-fatal → falls through to /spaces/new.
      let contributions = [];
      if (spaces.length === 0 && !onInviteFlow && !onContributing && !onAddMemory) {
        try {
          const contribData = await api.get('/me/contributions');
          contributions = contribData.contributions || [];
        } catch (e) {
          console.warn('contributions lookup failed during bootstrap:', e);
        }
      }

      setAppState({
        bootstrapped: true,
        bootstrapError: null,
        userId: meData.userId,
        displayName: meData.displayName || null,
        email: meData.email || null,
        pilotRole: meData.pilotRole,
        groupId: meData.groupId,
        groupName: meData.groupName,
        consentDate: meData.consentDate || null,
        consentPolicyVersion: meData.consentPolicyVersion || null,
        onboardingComplete: meData.onboardingComplete || false,
        tourPromptDismissals: meData.tourPromptDismissals || 0,
        spaces,
        flags: spacesData.flags || {},   // v1.35: 2.0-B envelope; {} if absent
        currentSpace,
      });

      if (!onInviteFlow && !onContributing && !onAddMemory) {
        if (spaces.length === 0) {
          // NEW-16: route a spaceless contributor to their linked feed (looked up
          // above) instead of the /spaces/new owner-create surface (403).
          if (contributions.length > 0) {
            navigate(`/contributing/${contributions[0].spaceId}/memories`, { replace: true });
          } else if (meData.pilotRole === 'leader') {
            navigate('/leader', { replace: true }); // NEW-175 (B): provisioned leader, no spaces -> leader home
          } else {
            navigate('/spaces/new', { replace: true });
          }
        } else {
          navigate(`/spaces/${currentSpace.id}`, { replace: true });
        }
      }

    } catch (err) {
      console.error('Bootstrap error:', err);

      if (err.status === 404 && err.error === 'USER_NOT_FOUND') {
        // v1.38 (Layer 4, NEW-15): a contributor claiming an invite has no `users`
        // row until ContributorLandingPage runs POST /me/contributions/link. Don't
        // bounce a not-yet-linked identity off the invite/connect flow to /join
        // before that auto-link fires — mirrors the success-path /invite carve-out
        // (v1.28). Without this, the /join redirect races the auto-link and wins.
        const claimPath = window.location.pathname;
        if (claimPath.startsWith('/invite/') || claimPath.startsWith('/contributing/')) {
          setAppState(prev => ({ ...prev, bootstrapped: true }));
          return;
        }

        const groupId = sessionStorage.getItem('ana_groupId');
        const groupName = sessionStorage.getItem('ana_groupName');
        const collectedName = sessionStorage.getItem('ana_displayName');

        if (!groupId) {
          navigate('/join', { replace: true });
          setAppState(prev => ({ ...prev, bootstrapped: true }));
          return;
        }

        try {
          // v1.18: Include invite token if present (personal invite flow)
          const inviteToken = sessionStorage.getItem('ana_inviteToken');
          const joinPayload = {
            groupId,
            email: auth0User?.email || '',
            displayName: collectedName || auth0User?.name || auth0User?.nickname || '',
          };
          if (inviteToken) {
            joinPayload.inviteToken = inviteToken;
          }

          const joinData = await api.post('/pilot/join', joinPayload);

          sessionStorage.removeItem('ana_groupId');
          sessionStorage.removeItem('ana_groupName');
          sessionStorage.removeItem('ana_displayName');
          sessionStorage.removeItem('ana_inviteToken');

          setAppState({
            bootstrapped: true,
            bootstrapError: null,
            userId: joinData.userId,
            displayName: joinData.displayName || null,
            email: joinData.email || null,
            pilotRole: joinData.pilotRole,
            groupId: joinData.groupId,
            groupName: joinData.groupName,
            // v1.19: User has not consented yet — about to navigate to /consent.
            // ConsentPage v1.2 calls updateConsent() with DB-authoritative values
            // immediately after POST /pilot/consent succeeds, so these fill in
            // before the user can reach the Settings panel.
            consentDate: null,
            consentPolicyVersion: null,
            spaces: [],
            flags: {},   // v1.35: join response has no flag envelope; next bootstrap resolves it
            currentSpace: null,
          });

          navigate('/consent', { replace: true });
          return;
        } catch (joinErr) {
          console.error('Join error:', joinErr);

          // v1.17 Fix 5: 409 = user already exists (double-tap, race condition).
          // The first request created the user; retry /me to pick them up.
          if (joinErr.status === 409 && joinErr.error === 'USER_ALREADY_EXISTS') {
            try {
              const [retryMe, retrySpaces] = await Promise.all([
                api.get('/pilot/me'),
                api.get('/spaces'),
              ]);
              const spaces = retrySpaces.spaces || [];
              setAppState({
                bootstrapped: true,
                bootstrapError: null,
                userId: retryMe.userId,
                displayName: retryMe.displayName || null,
                email: retryMe.email || null,
                pilotRole: retryMe.pilotRole,
                groupId: retryMe.groupId,
                groupName: retryMe.groupName,
                consentDate: retryMe.consentDate || null,
                consentPolicyVersion: retryMe.consentPolicyVersion || null,
                onboardingComplete: retryMe.onboardingComplete || false,
                tourPromptDismissals: retryMe.tourPromptDismissals || 0,
                spaces,
                flags: retrySpaces.flags || {},   // v1.35: same as primary bootstrap path
                currentSpace: spaces[0] || null,
              });
              if (spaces.length === 0) {
                navigate('/spaces/new', { replace: true });
              } else {
                navigate(`/spaces/${spaces[0].id}`, { replace: true });
              }
              return;
            } catch (retryErr) {
              console.error('409 retry failed:', retryErr);
              // Fall through to generic error below
            }
          }

          // v1.17 Fix 5: 403 = access denied. Show specific empathetic message.
          // These are NOT transient — retrying won't help.
          if (joinErr.status === 403) {
            const JOIN_403_MESSAGES = {
              GROUP_NOT_ACTIVE: "This space isn't open just yet. Your group leader will know when it's ready.",
              EMAIL_NOT_ALLOWED: "We couldn't find your email in this space. Your group leader can add you, or help sort it out.",
              USER_DEACTIVATED: "Your access to this space is paused. Your group leader can help you with next steps.",
              EMAIL_MISMATCH: "The email you signed in with doesn't match this invite. Please sign in with the email address your invite was sent to.",
            };
            const message = JOIN_403_MESSAGES[joinErr.error] || null;
            setAppState(prev => ({
              ...prev,
              bootstrapped: true,
              bootstrapError: {
                code: joinErr.error || 'ACCESS_DENIED',
                message: message || "We weren't able to set up your account. Your group leader can help.",
                retryable: false,
              },
            }));
            return;
          }

          // All other join errors — generic retryable
          setAppState(prev => ({
            ...prev,
            bootstrapped: true,
            bootstrapError: {
              code: joinErr.error || 'JOIN_FAILED',
              message: null,
              retryable: true,
            },
          }));
          return;
        }
      }

      // v1.17: All other bootstrap errors — generic retryable
      setAppState(prev => ({
        ...prev,
        bootstrapped: true,
        bootstrapError: {
          code: err.error || 'BOOTSTRAP_FAILED',
          message: null,
          retryable: true,
        },
      }));
    }
  }, [getAccessTokenSilently, navigate, auth0User]);

  useEffect(() => {
    if (isAuthenticated && !appState.bootstrapped) {
      bootstrap();
    }
  }, [isAuthenticated, appState.bootstrapped, bootstrap]);

  // v1.12: Return both appState and setAppState so AppRoutes can create
  // the updateProfile callback without exposing raw setAppState in context.
  return { appState, setAppState };
}

// ─── Inner app (inside BrowserRouter + AuthProvider) ─────────────────────

function AppRoutes() {
  const { isLoading, isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  const [sessionTag] = useState(() => crypto.randomUUID());

  // NEW-122 fix (2026-09-27, second pass): this belongs in AppRoutes, not
  // useSessionBootstrap -- that hook only returns { appState, setAppState },
  // so anything declared inside it is invisible here. Moved after the first
  // fix attempt still referenced it from the wrong scope (same ReferenceError
  // class as the navigate bug, different identifier).
  // Resolved, ready-to-render message for a failed create-own-space attempt
  // (or null). Deliberately separate from appState.bootstrapError, which
  // drives a full-page takeover meant for a bootstrap-time failure -- this
  // is a small, recoverable, mid-session one instead.
  const [createSpaceError, setCreateSpaceError] = useState(null);

  // v1.12: Destructure both appState and setAppState from hook
  const { appState, setAppState } = useSessionBootstrap(isAuthenticated, getAccessTokenSilently, user);

  // DF-7 phase 2: flag the member (owner) experience so the warm-olive --moment-* tokens apply.
  // Positive allowlist — warm ONLY owner surfaces; contributor (/contributing/*, /contributor-consent-*)
  // and pre-auth pages carry nothing and keep the cool :root token defaults. Inert until a fill
  // consumes var(--moment-action*).
  const location = useLocation();
  // NEW-122 fix (2026-09-27 incident): AppRoutes needs its own navigate --
  // it is a different function/scope from useSessionBootstrap's navigate above.
  // handleCreateOwnSpace below (also declared in AppRoutes) depends on this.
  const navigate = useNavigate();
  // v1.43: settings overlay-on-feed (no flag) — render /settings as an overlay over
  // the page it was triggered from when navigated with state.backgroundLocation
  // (set by the footer gear). Direct /settings / refresh ⇒ no backgroundLocation ⇒ full page.
  const backgroundLocation = location.state?.backgroundLocation || null;
  useEffect(() => {
    const p = location.pathname;
    const isMember = p.startsWith('/spaces') || p.startsWith('/settings') || p.startsWith('/leader');
    if (isMember) document.body.dataset.experience = 'member';
    else delete document.body.dataset.experience;
  }, [location.pathname]);

  // NEW-93: when the user has opted in to "Remember my email on this device",
  // keep the stored email current for the next sign-in's login_hint pre-fill.
  // setRememberedEmail no-ops unless the opt-in flag is set.
  useEffect(() => {
    if (isAuthenticated && user?.email) setRememberedEmail(user.email);
  }, [isAuthenticated, user]);

  // v1.12: Narrow updateProfile action — whitelists displayName and email only.
  // Consumed by SettingsPage v2.6 AccountPanel after successful PATCH /pilot/me.
  // If a future developer adds a new editable field, they must also add it to
  // ALLOWED here. This is intentional friction to prevent accidental context
  // pollution from an unvetted field.
  const updateProfile = useCallback((updates) => {
    const ALLOWED = ['displayName', 'email'];
    setAppState(prev => {
      const filtered = Object.fromEntries(
        Object.entries(updates).filter(([k, v]) => ALLOWED.includes(k) && v !== undefined)
      );
      if (Object.keys(filtered).length === 0) return prev;
      return { ...prev, ...filtered };
    });
  }, [setAppState]);

  // A-4: Callback for SpacePage/CreateSpaceModal to update the spaces list
  // in context after creating a new space. Avoids re-fetching /spaces.
  const updateSpaces = useCallback((newSpaces) => {
    setAppState(prev => ({ ...prev, spaces: newSpaces }));
  }, [setAppState]);

  // v1.19: Narrow updateConsent action — whitelists consentDate and
  // consentPolicyVersion only. Consumed by ConsentPage v1.2 after a successful
  // POST /pilot/consent so the SettingsPage Account panel reflects the new
  // consent record immediately, no hard refresh required.
  //
  // Caller passes the createdAt value from the Lambda response (DB-authoritative
  // — comes from RETURNING created_at on the INSERT), and the policyVersion
  // the page just submitted. Same values the next /pilot/me fetch would return.
  //
  // Same intentional-friction whitelist pattern as updateProfile: any future
  // consent-related field must be explicitly added to ALLOWED here before it
  // can flow into context.
  const updateConsent = useCallback((updates) => {
    const ALLOWED = ['consentDate', 'consentPolicyVersion'];
    setAppState(prev => {
      const filtered = Object.fromEntries(
        Object.entries(updates).filter(([k, v]) => ALLOWED.includes(k) && v !== undefined)
      );
      if (Object.keys(filtered).length === 0) return prev;
      return { ...prev, ...filtered };
    });
  }, [setAppState]);

  // v1.32: Narrow updateOnboarding action — whitelists onboardingComplete /
  // tourPromptDismissals only. Consumed by SpacePage after the tour completes
  // (PATCH /pilot/onboarding) or a dismissal flips the flag, so the in-session
  // gate closes immediately and the tour prompt stops re-firing on every feed
  // remount (previously only a hard refresh cleared it).
  const updateOnboarding = useCallback((updates) => {
    const ALLOWED = ['onboardingComplete', 'tourPromptDismissals'];
    setAppState(prev => {
      const filtered = Object.fromEntries(
        Object.entries(updates).filter(([k, v]) => ALLOWED.includes(k) && v !== undefined)
      );
      if (Object.keys(filtered).length === 0) return prev;
      return { ...prev, ...filtered };
    });
  }, [setAppState]);

  // ─── v1.21: Lock screen state ────────────────────────────────────────
  // Lock state is App-level — not shared via context. Child components
  // don't need to know about the lock screen.
  const [locked, setLocked] = useState(() => sessionStorage.getItem('ana_locked') === 'true');
  const [lockReason, setLockReason] = useState(() => sessionStorage.getItem('ana_locked') === 'true' ? 'pin' : null);
  const [securitySettings, setSecuritySettings] = useState(null); // { hasPin, lockTimeoutSeconds }
  const lastHiddenAt = useRef(null);

  // v1.21: Fetch security settings after bootstrap completes.
  // Determines whether the lock screen should activate on background return.
  // Runs once after bootstrap; re-runs if userId changes (shouldn't happen
  // in normal flow, but handles edge cases like 409 recovery).
  useEffect(() => {
    if (!isAuthenticated || !appState.bootstrapped || appState.bootstrapError) return;
    if (!appState.userId) return;

    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const api = createApiClient(getAccessTokenSilently);
        const data = await api.get('/security/settings');
        if (!cancelled) {
          setSecuritySettings(data);
        }
      } catch (err) {
        // Non-fatal: if fetch fails, lock screen simply won't activate.
        // User can still use the app normally. Security settings will be
        // retried on next app load.
        console.warn('Failed to fetch security settings:', err.message);
      }
    };
    fetchSettings();
    return () => { cancelled = true; };
  }, [isAuthenticated, appState.bootstrapped, appState.bootstrapError, appState.userId, getAccessTokenSilently]);

  // v1.21: Visibilitychange listener — detects background/foreground transitions.
  // Decision logic (per build plan §2):
  //   1. Hidden < lockTimeout? → No lock, resume normally.
  //   2. Auth0 session expired? → Session expired screen (Tier 2).
  //   3. User has PIN set? → Lock screen with PIN pad (Tier 1).
  //   4. No PIN set? → No lock, resume normally.
  //
  // Token expiry check uses isAuthenticated (synchronous boolean maintained
  // by Auth0 SDK) rather than getAccessTokenSilently — per Auth0 guidance,
  // silent token refresh via iframe is unreliable (fails on Safari,
  // localhost, and privacy-restricted browsers). If isAuthenticated is
  // false, the session is gone. If true and the token happens to be stale,
  // it will refresh naturally on the next API call after PIN unlock.
  useEffect(() => {
    if (!isAuthenticated || !appState.bootstrapped) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt.current = Date.now();
        return;
      }

      // visibilityState === 'visible' — user returned
      if (!lastHiddenAt.current) return;

      const elapsed = Date.now() - lastHiddenAt.current;
      lastHiddenAt.current = null;

      // Step 1: Under timeout threshold → no lock
      const timeoutMs = (securitySettings?.lockTimeoutSeconds || 300) * 1000;
      if (elapsed < timeoutMs) return;

      // Step 2: Check if Auth0 session is still active
      if (!isAuthenticated) {
        setLockReason('session_expired');
        setLocked(true);
        return;
      }

      // Step 3: User has PIN? → lock (Tier 1)
      if (securitySettings?.hasPin) {
        sessionStorage.setItem('ana_locked', 'true');
        setLockReason('pin');
        setLocked(true);
        return;
      }

      // Step 4: No PIN → no lock, resume normally
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, appState.bootstrapped, securitySettings]);

  // v1.21: Unlock handler — called by LockScreen after successful PIN verification.
  const handleUnlock = useCallback(() => {
    sessionStorage.removeItem('ana_locked');
    setLocked(false);
    setLockReason(null);
  }, []);

  // v1.22: Session expired handler — called by LockScreen when PIN verify
  // returns 401/403 (JWT expired). One-way transition: once set to
  // 'session_expired', cannot revert to 'pin' within the same lock cycle.
  // Only handleUnlock (full unlock → re-lock) can reset to 'pin'.
  const handleSessionExpired = useCallback(() => {
    sessionStorage.removeItem('ana_locked');
    setLockReason('session_expired');
  }, []);

  // v1.41 (NEW-21): PIN locked handler — called by LockScreen when the server
  // reports PIN_LOCKED_RESET_REQUIRED. Five wrong tries locks the PIN until a
  // NEW one is set; time does not clear it (anamoria-security v1.1). Same
  // one-way shape as handleSessionExpired: only handleUnlock can reset to
  // 'pin'. A separate reason from 'session_expired' on purpose — the person is
  // being asked to sign in again for a different cause, and the two screens say
  // different things.
  const handlePinLocked = useCallback(() => {
    sessionStorage.removeItem('ana_locked');
    setLockReason('pin_locked');
  }, []);

  // v1.21: API client for LockScreen PIN verification.
  // Same pattern as SettingsPage getApi.
  const getApiForLock = useCallback(
    () => createApiClient(getAccessTokenSilently),
    [getAccessTokenSilently]
  );

  // NEW-122: self-serve contributor->member conversion, triggered from
  // ContributorFeedPage's "Start your own space" button (pure contributor
  // only, gated server-side by contributor_conversion_enabled). Reuses the
  // existing /pilot/join endpoint and the existing consent-navigation
  // sequence -- see plan Product-and-Pilot/new122-contributor-to-member-
  // conversion-fix-plan_v0.14.md, section 6.2.
  const handleCreateOwnSpace = useCallback(async () => {
    setCreateSpaceError(null);
    const api = createApiClient(getAccessTokenSilently);
    try {
      const joinData = await api.post('/pilot/join', {});

      // Partial merge (NEW-122 plan section 6.2, v0.9): the join response here
      // carries only { userId, pilotRole, groupId } -- no displayName/email --
      // so a full reset (as the brand-new-member bootstrap path does) would
      // blank those two fields on this converting contributor's own screen.
      // Touch only what actually changed.
      setAppState(prev => ({
        ...prev,
        pilotRole: joinData.pilotRole,
        groupId: joinData.groupId,
        consentDate: null,
        consentPolicyVersion: null,
        spaces: [],
        flags: {},
        currentSpace: null,
      }));
      navigate('/consent', { replace: true });
    } catch (err) {
      console.error('Create-own-space conversion error:', err);

      // NEW-122: small, calm, below-the-header notice on ContributorFeedPage --
      // not the full-page appState.bootstrapError takeover, which is reserved
      // for a bootstrap-time failure, not a recoverable mid-session one.
      const CREATE_SPACE_ERROR_MESSAGES = {
        CONVERSION_DISABLED: "This isn't available just yet. Please check back soon.",
        USER_DEACTIVATED: (
          <>
            Your access to Anamoria is paused for now. If you have any questions,
            you can reach us at{' '}
            <a href="mailto:support@anamoria.org">support@anamoria.org</a>.
          </>
        ),
      };
      setCreateSpaceError(
        CREATE_SPACE_ERROR_MESSAGES[err.error] || "We weren't able to start that. Please try again."
      );
    }
  }, [getAccessTokenSilently, navigate]);

  // v1.21: Callback for SecurityPanel to update security settings in App state
  // after user sets/removes PIN or changes lock timeout. Avoids requiring
  // a full page reload for the lock screen to pick up changes.
  const updateSecuritySettings = useCallback((updates) => {
    setSecuritySettings(prev => prev ? { ...prev, ...updates } : updates);
  }, []);

  const contextValue = useMemo(
    () => ({ ...appState, sessionTag, updateProfile, updateSpaces, updateConsent, updateOnboarding, updateSecuritySettings }),
    [appState, sessionTag, updateProfile, updateSpaces, updateConsent, updateOnboarding, updateSecuritySettings]
  );

  if (isLoading) {
    return <ButterflyLoader />;
  }

  if (isAuthenticated && !appState.bootstrapped) {
    return <ButterflyLoader />;
  }

  if (isAuthenticated && appState.bootstrapError) {
    const { message, retryable } = appState.bootstrapError;

    // v1.17 Fix 5: Non-retryable errors (403 join failures) —
    // show specific message + "Start over" link. No retry button.
    if (!retryable && message) {
      return (
        <div className="app-error">
          <p className="app-error-message">{message}</p>
          <a
            className="app-error-link"
            href="/join"
            onClick={(e) => {
              e.preventDefault();
              // Clear stale session data so the join flow starts fresh
              sessionStorage.removeItem('ana_groupId');
              sessionStorage.removeItem('ana_groupName');
              sessionStorage.removeItem('ana_displayName');
              sessionStorage.removeItem('ana_inviteToken');
              window.location.href = '/join';
            }}
          >
            Start over with a different code
          </a>
        </div>
      );
    }

    // v1.17 Fix 5: Retryable errors (500, network, unknown) —
    // updated copy per Error Message Catalog v1.0, Category 3.
    return (
      <div className="app-error">
        <h2>We couldn't open Anamoria just now.</h2>
        <p>Your memories are safe. Try once more, or come back in a few minutes.</p>
        <button
          className="app-error-btn"
          onClick={() => window.location.reload()}
        >
          Try once more
        </button>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      {/* v1.21→v1.22: Lock screen overlay — renders above all routes when locked.
          Solid warm beige background hides all content underneath.
          Two modes: PIN pad (Tier 1) or session expired (Tier 2).
          v1.22: onSessionExpired transitions from PIN to session expired on 401/403. */}
      {locked && (
        <LockScreen
          lockReason={lockReason}
          pinLength={securitySettings?.pinLength ?? null}
          onUnlock={handleUnlock}
          onSessionExpired={handleSessionExpired}
          onPinLocked={handlePinLocked}
          getApi={getApiForLock}
        />
      )}
      <Suspense fallback={<LoadingFallback />}>
        <Routes location={backgroundLocation || location}>
          {/* ── Public ───────────────────────────────────────────── */}
          <Route path="/join" element={<JoinPage />} />
          {/* v1.18: Personal invite token route */}
          <Route path="/join/invite/:token" element={<JoinPage />} />
          {/* v1.26: /privacy — public Privacy Policy (LegalPage). Bundled
              content renders offline via the SPA fallback. /terms reserved
              until the Terms is finalized. */}
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          {/* NS-61/O-b: public, versioned contributor-consent record pages (self-proving;
              each is byte-identical to the file whose sha256 is in policy_documents).
              Record/reference pages only — NOT part of the claim flow. */}
          <Route path="/contributor-consent-group" element={<LegalPage doc="contributor-consent-group" />} />
          <Route path="/contributor-consent-member" element={<LegalPage doc="contributor-consent-member" />} />

          {/* ── Protected ─────────────────────────────────────────── */}
          <Route
            path="/consent"
            element={
              <ProtectedRoute>
                <ConsentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/new"
            element={
              <ProtectedRoute>
                <CreateSpacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId"
            element={
              <ProtectedRoute>
                <SpacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/invite/bulk"
            element={
              <ProtectedRoute>
                <BulkInvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/record"
            element={
              <ProtectedRoute>
                <RecordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/write"
            element={
              <ProtectedRoute>
                <WritePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/photo"
            element={
              <ProtectedRoute>
                <PhotoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/video"
            element={
              <ProtectedRoute>
                <VideoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/add"
            element={
              <ProtectedRoute>
                <AddMemoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/memories/:memId"
            element={
              <ProtectedRoute>
                <MemoryDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/moments/:momentId"
            element={
              <ProtectedRoute>
                <MomentDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/invite"
            element={
              <ProtectedRoute>
                <InvitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/reminder"
            element={
              <ProtectedRoute>
                <ReminderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spaces/:spaceId/feedback"
            element={
              <ProtectedRoute>
                <FeedbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leader"
            element={
              <ProtectedRoute>
                <LeaderPage />
              </ProtectedRoute>
            }
          />

          {/* ── Contributor claim (public: invite validation only) ───── */}
          {/* Track B (B-2): the anonymous session-token path is retired. The old
              public /contribute/:spaceId[...] routes (ContributorHomePage + the
              session-mode capture pages) are removed; contributors sign in and
              use the LINKED /contributing/* routes below. /invite/:token stays
              public — it only validates the invite to render the claim screen. */}
          <Route path="/invite/:token" element={<ContributorLandingPage />} />

          {/* ── Contributor LINKED routes (JWT auth — the only contributor rail) ──
              For logged-in users who linked their account to an invite. These
              are the only contributor routes; the pages run in mode="linked" and
              use the owner JWT client + the
              /me/contributions API prefix (see api/contributorAccess.js). The
              feed is the entry point — no linked "home" route by design.
              ProtectedRoute enforces JWT; the backend also authorizes off
              linked_user_id, so this is defense-in-depth. */}
          <Route
            path="/contributing/:spaceId/memories"
            element={
              <ProtectedRoute>
                <ContributorFeedPage
                  mode="linked"
                  mySpaceId={appState.currentSpace?.id || appState.spaces?.[0]?.id || null}
                  onCreateOwnSpace={handleCreateOwnSpace}
                  createSpaceError={createSpaceError}
                />
              </ProtectedRoute>
            }
          />
          {/* v1.44 NEW-65 (PR-7b): read-only detail for a SHARED memory the contributor
              did not author. Memory passed via router state (Option A); the page redirects
              to the feed if state is absent (refresh / deep link). More-specific than the
              /memories feed route, so React Router ranks it first regardless of order. */}
          <Route
            path="/contributing/:spaceId/memories/:memId"
            element={
              <ProtectedRoute>
                <ContributorMemoryDetailPage mode="linked" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contributing/:spaceId/record"
            element={
              <ProtectedRoute>
                <ContributorRecordPage mode="linked" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contributing/:spaceId/write"
            element={
              <ProtectedRoute>
                <ContributorWritePage mode="linked" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contributing/:spaceId/photo"
            element={
              <ProtectedRoute>
                <ContributorPhotoPage mode="linked" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contributing/:spaceId/video"
            element={
              <ProtectedRoute>
                <ContributorVideoPage mode="linked" />
              </ProtectedRoute>
            }
          />

          {/* ── Settings + Billing (B1–B3) ───────────────────────── */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/upgrade"
            element={
              <ProtectedRoute>
                <UpgradePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/upgrade/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/upgrade/success"
            element={
              <ProtectedRoute>
                <UpgradeSuccessPage />
              </ProtectedRoute>
            }
          />
          {/* v1.14: Phase D — request history */}
          <Route
            path="/settings/my-requests"
            element={
              <ProtectedRoute>
                <MyRequestsPage />
              </ProtectedRoute>
            }
          />

          {/* v1.33: Root route — resolves navigate('/') (incl. browser Back to
              the app root) to the user's space instead of falling through to the
              /join catch-all (which was logging authenticated users out). Only
              evaluates post-bootstrap; the routes are gated by the loading screen. */}
          <Route path="/" element={
            !isAuthenticated
              ? <Navigate to="/join" replace />
              : (appState.spaces && appState.spaces.length > 0
                  ? <Navigate to={`/spaces/${appState.currentSpace?.id || appState.spaces[0].id}`} replace />
                  : <Navigate to="/spaces/new" replace />)
          } />

          {/* ── Fallback ──────────────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/join" replace />} />
        </Routes>
        {/* v1.42: settings overlay-on-feed — modal route rendered on top of the
            backgroundLocation (the feed). Only when the flag set a backgroundLocation. */}
        {backgroundLocation && (
          <Routes>
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsOverlay><SettingsPage inOverlay /></SettingsOverlay>
              </ProtectedRoute>
            } />
          </Routes>
        )}
      </Suspense>
      {/* v1.16: PWA update banner — renders null unless new SW detected */}
      <PWAUpdatePrompt />
      {/* v1.24: Install guides — gated on onboardingComplete so they never
          interrupt first-time onboarding. Each self-gates by platform,
          standalone mode, and localStorage dismissal. */}
      {appState.onboardingComplete && (
        <>
          <IOSInstallPrompt />
          <AndroidInstallPrompt />
        </>
      )}
    </AppContext.Provider>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
